import { useCallback, useEffect, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { PhotoDetail } from '@/types/photo';
import { getAllAlbums, type AlbumData } from '@/services/albumService';
import { searchPhotos, setPhotoFavorite } from '@/services/photoService';

type SearchFilters = {
  query: string;
  albumId: string;
  shotDate: string;
  favoriteOnly: boolean;
};

function filtersFromParams(params: URLSearchParams): SearchFilters {
  return {
    query: params.get('q') || '',
    albumId: params.get('albumId') || '',
    shotDate: params.get('shotDate') || '',
    favoriteOnly: params.get('favoriteOnly') === 'true',
  };
}

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<SearchFilters>(() => filtersFromParams(searchParams));
  const [photos, setPhotos] = useState<PhotoDetail[]>([]);
  const [albums, setAlbums] = useState<AlbumData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingFavoriteId, setUpdatingFavoriteId] = useState<string | null>(null);

  useEffect(() => {
    void getAllAlbums().then(setAlbums).catch(() => setAlbums([]));
  }, []);

  const loadPhotos = useCallback(async (nextFilters: SearchFilters) => {
    setLoading(true);
    setError('');
    try {
      const result = await searchPhotos({
        query: nextFilters.query,
        albumId: nextFilters.albumId || undefined,
        shotDate: nextFilters.shotDate || undefined,
        favoriteOnly: nextFilters.favoriteOnly,
        limit: 100,
      });
      setPhotos(result);
    } catch {
      setError('사진을 찾지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPhotos(filters);
  }, [filters, loadPhotos]);

  const updateFilters = (changes: Partial<SearchFilters>) => {
    const next = { ...filters, ...changes };
    setFilters(next);

    const params = new URLSearchParams();
    if (next.query.trim()) params.set('q', next.query.trim());
    if (next.albumId) params.set('albumId', next.albumId);
    if (next.shotDate) params.set('shotDate', next.shotDate);
    if (next.favoriteOnly) params.set('favoriteOnly', 'true');
    setSearchParams(params, { replace: true });
  };

  const handleFavorite = async (event: MouseEvent<HTMLButtonElement>, photo: PhotoDetail) => {
    event.preventDefault();
    event.stopPropagation();
    if (updatingFavoriteId) return;

    setUpdatingFavoriteId(photo.id);
    try {
      const updated = await setPhotoFavorite(photo.id, !photo.isFavorite);
      if (!updated) return;
      setPhotos((current) => current
        .map((item) => (item.id === photo.id ? updated : item))
        .filter((item) => !filters.favoriteOnly || item.isFavorite));
    } finally {
      setUpdatingFavoriteId(null);
    }
  };

  const clearFilters = () => updateFilters({
    query: '', albumId: '', shotDate: '', favoriteOnly: false,
  });

  const slideshowParams = new URLSearchParams(searchParams);
  slideshowParams.set('source', 'search');

  return (
    <div className="min-h-screen bg-background-50 pb-8">
      <header className="sticky top-0 z-20 border-b border-background-200/60 bg-background-50/95 backdrop-blur-sm">
        <div className="flex items-center gap-2 px-4 py-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-secondary-100"
            aria-label="뒤로 가기"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-bold text-foreground-950">사진 찾기</h1>
            <p className="text-[11px] text-foreground-400">제목·파일명·앨범·촬영일</p>
          </div>
          {photos.length > 0 && (
            <button
              type="button"
              onClick={() => navigate(`/slideshow?${slideshowParams.toString()}`)}
              className="inline-flex items-center gap-1 rounded-full bg-foreground-900 px-3 py-2 text-xs font-medium text-background-50"
            >
              <i className="ri-slideshow-3-line text-sm" />
              슬라이드쇼
            </button>
          )}
        </div>
      </header>

      <section className="px-4 py-4">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            updateFilters({ query: filters.query });
          }}
          className="relative"
        >
          <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-foreground-400" />
          <input
            value={filters.query}
            onChange={(event) => updateFilters({ query: event.target.value })}
            placeholder="사진 제목이나 파일명을 입력하세요"
            className="h-12 w-full rounded-2xl border border-background-200 bg-secondary-50 py-2 pl-10 pr-10 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-400"
          />
          {filters.query && (
            <button
              type="button"
              onClick={() => updateFilters({ query: '' })}
              className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-foreground-400 hover:bg-background-200"
              aria-label="검색어 지우기"
            >
              <i className="ri-close-line text-lg" />
            </button>
          )}
        </form>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <select
            value={filters.albumId}
            onChange={(event) => updateFilters({ albumId: event.target.value })}
            className="h-10 min-w-0 rounded-xl border border-background-200 bg-background-50 px-3 text-xs text-foreground-700 outline-none focus:border-accent-400"
            aria-label="앨범 선택"
          >
            <option value="">모든 앨범</option>
            {albums.map((album) => <option key={album.id} value={album.id}>{album.title}</option>)}
          </select>
          <input
            type="month"
            value={filters.shotDate}
            onChange={(event) => updateFilters({ shotDate: event.target.value })}
            className="h-10 min-w-0 rounded-xl border border-background-200 bg-background-50 px-3 text-xs text-foreground-700 outline-none focus:border-accent-400"
            aria-label="촬영월 선택"
          />
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => updateFilters({ favoriteOnly: !filters.favoriteOnly })}
            className={`inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-semibold transition-colors ${
              filters.favoriteOnly
                ? 'bg-red-50 text-red-500 ring-1 ring-red-200'
                : 'bg-secondary-100 text-foreground-500'
            }`}
          >
            <i className={`${filters.favoriteOnly ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`} />
            즐겨찾기만
          </button>
          {(filters.query || filters.albumId || filters.shotDate || filters.favoriteOnly) && (
            <button
              type="button"
              onClick={clearFilters}
              className="h-9 rounded-full px-3 text-xs font-medium text-foreground-500 hover:bg-secondary-100"
            >
              필터 초기화
            </button>
          )}
        </div>
      </section>

      <section className="px-4 pb-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-foreground-800">검색 결과 <span className="font-normal text-foreground-400">{loading ? '' : `${photos.length}장`}</span></p>
          {!loading && photos.length >= 100 && <span className="text-[11px] text-foreground-400">최대 100장 표시</span>}
        </div>
      </section>

      {loading ? (
        <div className="grid grid-cols-2 gap-2 px-4">
          {[1, 2, 3, 4, 5, 6].map((item) => <div key={item} className="aspect-square animate-pulse rounded-xl bg-secondary-100" />)}
        </div>
      ) : error ? (
        <div className="px-4 py-16 text-center">
          <i className="ri-error-warning-line text-3xl text-foreground-300" />
          <p className="mt-3 text-sm text-foreground-500">{error}</p>
          <button type="button" onClick={() => void loadPhotos(filters)} className="mt-4 rounded-full bg-accent-500 px-4 py-2 text-xs font-medium text-background-50">다시 시도</button>
        </div>
      ) : photos.length === 0 ? (
        <div className="px-4 py-16 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100"><i className="ri-search-line text-2xl text-foreground-400" /></div>
          <p className="mt-4 text-sm font-medium text-foreground-600">찾는 사진이 없어요</p>
          <p className="mt-1 text-xs text-foreground-400">검색어 또는 앨범·촬영월을 바꿔 보세요.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 px-4 pb-4">
          {photos.map((photo) => (
            <div
              key={photo.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/albums/${photo.albumId}/photos/${photo.id}`)}
              onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(`/albums/${photo.albumId}/photos/${photo.id}`);
                }
              }}
              className="group min-w-0 cursor-pointer text-left"
            >
              <div className="relative aspect-square overflow-hidden rounded-xl bg-secondary-100">
                <img src={photo.image} alt={photo.caption || '사진'} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                {photo.mediaType === 'video' && <i className="ri-play-circle-fill absolute inset-0 m-auto h-10 w-10 text-4xl text-white drop-shadow-lg" />}
                <button
                  type="button"
                  onClick={(event) => void handleFavorite(event, photo)}
                  disabled={updatingFavoriteId === photo.id}
                  className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur-sm disabled:opacity-60"
                  aria-label={photo.isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                >
                  <i className={`${photo.isFavorite ? 'ri-heart-fill text-red-300' : 'ri-heart-line'} text-lg`} />
                </button>
              </div>
              <p className="mt-1.5 truncate text-xs font-medium text-foreground-800">{photo.caption || photo.originalFilename}</p>
              <p className="truncate text-[10px] text-foreground-400">{photo.albumTitle ? `${photo.albumTitle} · ` : ''}{photo.shotDate}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
